import { useState, useEffect, useCallback } from 'react';
import { callRpc } from '../../lib/rpcClient';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { subDays } from 'date-fns';
import { RotateCcw } from 'lucide-react';

export function AdminAnalytics() {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      // Default to last 30 days
      const end = new Date();
      const start = subDays(end, 30);
      
      const result = await callRpc('get_admin_user_kpis', {
        p_from: start.toISOString(),
        p_to: end.toISOString()
      });
      
      setData(result);
    } catch (err: any) {
      setError(err.message || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (loading) {
    return (
      <div className="flex justify-center p-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-500/10 border border-red-500/20 text-red-500 p-6 rounded-2xl w-full text-center">
        {error}
      </div>
    );
  }

  // Formatting data for funnel chart
  const funnelData = [
    { name: 'Registered', value: data.new_users },
    { name: 'Added Car', value: data.users_with_cars },
    { name: 'Added Expense', value: data.users_with_first_expense },
    { name: 'Used Email Ingestion', value: data.users_with_email_ingestion },
  ];

  return (
    <div className="space-y-8 font-primary pb-12">
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-black tracking-tight text-white mb-1">Analytics</h1>
          <p className="text-slate-400">User behavior and conversion funnels (Last 30 Days).</p>
        </div>
        <button 
          onClick={() => loadData()}
          disabled={loading}
          className="p-3 border border-slate-800 bg-slate-900 rounded-2xl hover:bg-slate-800 disabled:opacity-30 transition-all shadow-sm active:scale-95 text-slate-500 hover:text-blue-400 group"
          title="Refresh"
        >
          <RotateCcw size={20} className={loading ? 'animate-spin' : 'group-hover:rotate-[-30deg] transition-transform'} />
        </button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 flex flex-col items-center justify-center text-center">
          <div className="text-slate-400 mb-2 font-bold uppercase text-sm tracking-wider">New Users</div>
          <div className="text-4xl font-black text-white">{data.new_users}</div>
        </div>
        
        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 flex flex-col items-center justify-center text-center relative overflow-hidden">
          <div className="absolute top-0 right-0 w-16 h-16 bg-blue-500/10 blur-xl rounded-full"></div>
          <div className="text-slate-400 mb-2 font-bold uppercase text-sm tracking-wider">7-Day Retention</div>
          <div className="text-4xl font-black text-blue-400">{data.retention_7d_percent}%</div>
          <div className="text-slate-500 text-xs mt-2">{data.retention_7d_absolute} users returned</div>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6">
          <h3 className="font-bold text-white mb-6">User Conversion Funnel</h3>
          <div className="h-[300px]">
             <ResponsiveContainer width="100%" height="100%">
               <BarChart 
                 layout="vertical"
                 data={funnelData} 
                 margin={{ top: 5, right: 30, left: 40, bottom: 5 }}
               >
                 <CartesianGrid strokeDasharray="3 3" stroke="#334155" horizontal={false} />
                 <XAxis type="number" stroke="#94a3b8" tick={{ fill: '#94a3b8' }} />
                 <YAxis dataKey="name" type="category" stroke="#94a3b8" tick={{ fill: '#e2e8f0', fontSize: 12, fontWeight: 'bold' }} />
                 <Tooltip 
                   cursor={{ fill: '#1e293b' }}
                   contentStyle={{ backgroundColor: '#0f172a', border: '1px solid #334155', borderRadius: '12px', color: '#fff' }}
                   itemStyle={{ color: '#fff' }}
                 />
                 <Bar dataKey="value" fill="#3b82f6" radius={[0, 4, 4, 0]} barSize={32} />
               </BarChart>
             </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
}
